import React, { useEffect, useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

// Quantum
import { AnalyticsContext } from '../analytics/AnalyticsContext';

// Components
import {
  BannerAdStatic,
  BannerAdDynamic,
  Carousel,
  Layout,
  SEO,
  Swimlane,
  ShopBannerCTA,
} from '../components';

// Utils
import {
  extractStrapiCarouselItems,
  splitSwimlanes,
  injectTrailers,
} from '../utils/helpers';
import LazyLoad from 'react-lazy-load';
import zip from 'lodash/zip';

// Styles
import './page.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
let tealiumTrackPageLoad;
if (typeof window !== 'undefined') {
  const { trackPageLoad } = initTealiumTracker({ debugMode: true });
  tealiumTrackPageLoad = trackPageLoad;
}

// GraphQL Query

const MoviesPage = ({ pageContext }: any) => {
  const data = useStaticQuery(graphql`
    query MoviesPage {
      allStrapi {
        moviesPage {
          carousel {
            Slide {
              ...SlideFragment
            }
          }
          Advertisement {
            ...BannerAdFragment
          }
        }
      }

      allSOD {
        freeMovieTrailers: carouselTrailers(curation: "MoviesFree", limit: 3) {
          id
          hls
          dash
          mp4
        }

        videoRentalTrailers: carouselTrailers(
          curation: "MoviesTopVideoRentals"
          limit: 3
        ) {
          id
          hls
          dash
          mp4
        }
      }
    }
  `);

  const { analytics } = useContext(AnalyticsContext);
  const { curations } = pageContext;

  const { freeMovieTrailers, videoRentalTrailers } = data.allSOD;

  const { carousel, Advertisement } = data.allStrapi.moviesPage;

  // Carousel
  const videoRentalsSwimlane = curations.find(
    (curation) => curation.name === 'MoviesTopVideoRentals'
  );
  const freeMoviesSwimlane = curations.find(
    (curation) => curation.name === 'MoviesFree'
  );

  // Grabs first 3 items from swimlanes and zippers them into one array for Carousel
  const swimlaneCarouselItems =
    videoRentalsSwimlane && freeMoviesSwimlane
      ? zip(
          injectTrailers(
            videoRentalsSwimlane.items.slice(0, 3),
            videoRentalTrailers
          ),
          injectTrailers(
            freeMoviesSwimlane.items.slice(0, 3),
            freeMovieTrailers
          )
        ).flat()
      : [];

  const strapiCarouselItems = extractStrapiCarouselItems(carousel);
  const slides = [...swimlaneCarouselItems, ...strapiCarouselItems];

  // Swimlanes
  const { firstSwimlaneHalf, secondSwimlaneHalf } = splitSwimlanes(curations);

  // Quantum Tracking for page view
  useEffect(() => {
    if (analytics) {
      analytics.track('SpectrumOnDemand_pageView_movies', {
        currPageIsLazyLoad: true,
      });
    }
  }, [analytics]);

  // Tealium Page Tracking
  useEffect(() => {
    tealiumTrackPageLoad({
      pagename: 'Movies Page',
      section: 'Movies',
      pagetype: 'Movies',
      sc_reporting_suite: 'charterspectrumondemand',
    });
  }, []);

  const analyticsConfig = {
    analytics,
    eventCase:
      'SpectrumOnDemand_selectContent_curatedCatalog_movies_assetSelection',
  };

  return (
    <Layout
      header={<Carousel slides={slides} analytics={analytics} />}
      bottomBanner={
        <LazyLoad offsetVertical={250} throttle={250} debounce={false}>
          <BannerAdStatic version="spectrumGoes" />
        </LazyLoad>
      }
    >
      <SEO
        title="Watch On Demand Movies"
        description="Watch new, free, and recently released movies and trailers with Spectrum On Demand."
      />

      <ShopBannerCTA
        title="Interested in becoming a Spectrum TV Customer?"
        linkPath="https://www.spectrum.com/cable-tv/plans?cmp=sod_shop_movies"
        tealiumConfig={{
          dataLinkType: 'shop-banner__primary-button',
          dataLinkName: `Shop Banner - Shop Spectrum's Plans`,
        }}
      />

      <h1>Movies</h1>

      {/* Swimlanes */}

      {firstSwimlaneHalf.map(({ displayName, items }) => (
        <Swimlane
          key={displayName}
          title={displayName}
          items={items}
          analyticsConfig={analyticsConfig}
        />
      ))}

      <BannerAdDynamic
        ad={Advertisement.banner_ad}
        size={Advertisement.banner_ad.Size}
      />
      {secondSwimlaneHalf.map(({ displayName, items }) => (
        <Swimlane
          key={displayName}
          title={displayName}
          items={items}
          analyticsConfig={analyticsConfig}
        />
      ))}
    </Layout>
  );
};

export default MoviesPage;
